import Badge from '@precis-digital/kurama/Badge';
import { type MultiSelectDropdownItem } from '@precis-digital/kurama/MultiSelectDropdown';
import { type TooltipProps } from '@precis-digital/kurama/Tooltip';
import ChartBadge from '../components/ChartBadge';
import type { ElementsDescriptionsMap } from '../internalTypes';
import type { ChartCardTFunction, ChartChoiceOption } from '../types';
import { elementDescriptionIsDimension } from './elementDescription';

/**
 * Visible dimension columns, in picker order — the grain a server fetch must group by.
 * Returns `undefined` when the table has no column picker, so callers can keep their own default.
 */
export const getSelectedDimensionColumnIds = <DataKey extends string,>({
  selectedColumnsIds,
  elementsDescriptionsMap
}: {
  selectedColumnsIds: DataKey[] | undefined;
  elementsDescriptionsMap: ElementsDescriptionsMap<DataKey>;
}): DataKey[] | undefined => selectedColumnsIds?.filter(columnId => elementDescriptionIsDimension(elementsDescriptionsMap[columnId]));
export interface BuildColumnsSelectionOptionsParams<DataKey extends string> {
  options: Array<ChartChoiceOption<DataKey>>;
  selectedColumnsIds: DataKey[];
  elementsDescriptionsMap: ElementsDescriptionsMap<DataKey>;
  useColumnsDecorations?: boolean;
  t: ChartCardTFunction;
}
export const buildColumnsSelectionOptions = <DataKey extends string,>({
  options,
  selectedColumnsIds,
  elementsDescriptionsMap,
  useColumnsDecorations = false,
  t
}: BuildColumnsSelectionOptionsParams<DataKey>): MultiSelectDropdownItem[] => {
  const selectedDimensionsCount = selectedColumnsIds.filter(columnId => elementDescriptionIsDimension(elementsDescriptionsMap[columnId])).length;
  return options.reduce<MultiSelectDropdownItem[]>((acc, {
    value,
    label,
    icon,
    description,
    color,
    pattern,
    ...props
  }) => {
    const isSelected = selectedColumnsIds.includes(value);
    if (useColumnsDecorations && color != null) {
      if (pattern == null) {
        icon = <Badge color={color} variant="lighter" />;
      } else {
        icon = <ChartBadge chartType="line" color={color} pattern={pattern} />;
      }
    }
    const isDimension = elementDescriptionIsDimension(elementsDescriptionsMap[value]);
    const isLastSelectedDimension = isSelected && isDimension && selectedDimensionsCount === 1;
    const isDisabled = props.disabled === true || isLastSelectedDimension;
    const undeselectableDescription = isLastSelectedDimension ? t('cannotRemoveLastDimension') : undefined;
    const columnDescription = description == null || description === label ? undefined : description;
    const tooltipKind = columnDescription == null || undeselectableDescription == null ? 'singleline' : 'multiline';
    const tooltipTitle = `${columnDescription ?? ''}${tooltipKind === 'multiline' ? '\n' : ''}${undeselectableDescription ?? ''}`;
    const tooltipProps: TooltipProps | undefined = tooltipTitle === '' ? undefined : {
      kind: tooltipKind,
      title: tooltipTitle
    };
    return [...acc, {
      label: label ?? value,
      value,
      tooltipProps,
      icon,
      ...props,
      disabled: isDisabled
    }];
  }, []);
};